// Sales settings (2E-1a): company, payment details (bank account masked by default), logo + payment QR, defaults,
// read-only numbering. Saves only the fields that changed (sales_update_settings; optimistic version).
import { useState, useEffect, useMemo } from "react";
import { FormSection, Field, Input, TextArea, Button, Notice, DocumentUploader, DetailList } from "../ui/kit/components";
import { T, text, fmtDateTime } from "../ui/kit/tokens";
import { useMobile } from "../ui/core";
import { saveSettings, uploadSettingsImage, signedUrl, fiscalYearOf, findStampPath, errText } from "./api";
import { SectionCard, panError, emailError, maskAccount, trim } from "./shared";

const FIELDS = ["company_name", "pan", "address", "phone", "email", "website", "bank_name", "bank_account_name", "bank_account_no",
                "bank_branch", "default_terms", "payment_terms_days", "quote_validity_days"];
const todayIso = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 10); };

function ImagePreview({ path, alt }) {
    const [url, setUrl] = useState(null);
    useEffect(() => { let live = true; signedUrl(path).then(u => { if (live) setUrl(u); }); return () => { live = false; }; }, [path]);
    if (!path) return <div style={text.caption}>None uploaded.</div>;
    return url ? <img src={url} alt={alt} style={{ maxWidth: 160, maxHeight: 160, objectFit: "contain", borderRadius: 8, border: `1px solid ${T.borderSoft}`, background: "#fff" }} />
        : <div style={text.caption}>Loading preview…</div>;
}

export default function SalesSettings({ settings, series, reload }) {
    const mobile = useMobile();
    const initial = useMemo(() => Object.fromEntries(FIELDS.map(k => [k, settings?.[k] ?? ""])), [settings]);
    const [f, setF] = useState(initial);
    const [showAcct, setShowAcct] = useState(false);
    const [logo, setLogo] = useState(null), [qr, setQr] = useState(null), [stamp, setStamp] = useState(null), [stampPath, setStampPath] = useState(undefined);
    const [busy, setBusy] = useState(false), [err, setErr] = useState(""), [ok, setOk] = useState("");
    const [fy, setFy] = useState(undefined);
    useEffect(() => { setF(initial); }, [initial]);
    useEffect(() => { fiscalYearOf(todayIso()).then(setFy); }, []);
    useEffect(() => { findStampPath().then(setStampPath).catch(() => setStampPath(null)); }, [settings]);
    const set = (k) => (e) => { setOk(""); setF(s => ({ ...s, [k]: e.target.value })); };

    const errors = {
        company_name: !trim(f.company_name) ? "The company name is required for documents." : "",
        pan: panError(f.pan),
        email: emailError(f.email),
        payment_terms_days: !/^\d+$/.test(String(f.payment_terms_days)) || +f.payment_terms_days > 365 ? "0–365 days." : "",
        quote_validity_days: !/^\d+$/.test(String(f.quote_validity_days)) || +f.quote_validity_days < 1 || +f.quote_validity_days > 365 ? "1–365 days." : "",
    };
    const invalid = Object.values(errors).some(Boolean);
    const changed = FIELDS.filter(k => String(trim(f[k]) ?? "") !== String(initial[k] ?? "")) ;
    const dirty = changed.length > 0 || logo || qr || stamp;

    const save = async () => {
        if (invalid) return;
        setBusy(true); setErr(""); setOk("");
        try {
            const p = {};
            for (const k of changed) p[k] = ["payment_terms_days", "quote_validity_days"].includes(k) ? Number(f[k]) : trim(f[k]);
            if (logo) p.logo_path = await uploadSettingsImage("logo", logo);
            if (qr) p.qr_path = await uploadSettingsImage("qr", qr);
            // The stamp is a file only (the newest one is used); it changes no settings field.
            if (stamp) { await uploadSettingsImage("stamp", stamp); setStampPath(await findStampPath()); }
            if (Object.keys(p).length) { p.reason = "Sales settings updated"; await saveSettings(p, settings.version); }
            setLogo(null); setQr(null); setStamp(null); setOk("Saved."); await reload();
        } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
    };

    const next = (type) => {
        const s = series.find(x => x.doc_type === type && x.fy_label === fy);
        const prefix = type === "INV" ? settings?.invoice_prefix : settings?.quote_prefix;
        return s ? `${prefix}-${s.fy_label}-${String(s.next_no).padStart(4, "0")}` : "—";
    };
    const grid = { display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr", gap: 14 };
    if (!settings) return <Notice tone="danger">Sales settings are missing (migration 014 not applied?).</Notice>;

    return (
        <div>
            <SectionCard title="Company" subtitle="Shown on quotations and invoices. Foxbeep is PAN-registered and not VAT-registered, so documents are not tax invoices and carry no VAT.">
                <FormSection>
                    <div style={grid}>
                        <Field label="Company name" required error={errors.company_name}><Input value={f.company_name} onChange={set("company_name")} /></Field>
                        <Field label="PAN" hint="9 digits" error={errors.pan}><Input inputMode="numeric" maxLength={9} value={f.pan} onChange={set("pan")} /></Field>
                        <Field label="Phone"><Input value={f.phone} onChange={set("phone")} /></Field>
                        <Field label="Email" error={errors.email}><Input type="email" value={f.email} onChange={set("email")} /></Field>
                        <Field label="Website"><Input value={f.website} onChange={set("website")} /></Field>
                        <Field label="VAT"><div style={{ ...text.caption, paddingTop: 10 }}>Not VAT-registered (fixed)</div></Field>
                    </div>
                    <Field label="Address"><TextArea value={f.address} onChange={set("address")} /></Field>
                    <div style={grid}>
                        <div><div style={text.label}>Current logo</div><ImagePreview path={settings.logo_path} alt="Company logo" /></div>
                        <DocumentUploader label="Replace logo (PNG, JPEG or WebP, max 2 MB)" accept="image/png,image/jpeg,image/webp" maxMB={2} file={logo} onChange={setLogo} />
                    </div>
                    <div style={grid}>
                        <div><div style={text.label}>Company stamp</div>{stampPath === undefined ? <div style={text.caption}>Loading…</div> : <ImagePreview path={stampPath} alt="Company stamp" />}
                            <div style={{ ...text.caption, marginTop: 6 }}>Printed beside the signature line on issued quotations and invoices (and their cancelled copies) and on receipts. Never on drafts.</div></div>
                        <DocumentUploader label="Replace stamp (transparent PNG or WebP, max 2 MB)" accept="image/png,image/webp" maxMB={2} file={stamp} onChange={setStamp} />
                    </div>
                </FormSection>
            </SectionCard>

            <SectionCard title="Payment details" subtitle="Printed on issued quotations and invoices so clients can pay. The full account number stays private in Sales settings.">
                <FormSection>
                    <div style={grid}>
                        <Field label="Bank"><Input value={f.bank_name} onChange={set("bank_name")} placeholder="Rastriya Banijya Bank" /></Field>
                        <Field label="Account name"><Input value={f.bank_account_name} onChange={set("bank_account_name")} /></Field>
                        <Field label="Account number" hint={showAcct ? "Visible — hide it when you're done." : `Hidden (${maskAccount(settings.bank_account_no)})`}>
                            <div style={{ display: "flex", gap: 8 }}>
                                <Input type={showAcct ? "text" : "password"} autoComplete="off" spellCheck={false} value={f.bank_account_no} onChange={set("bank_account_no")} style={{ flex: 1 }} />
                                <Button type="button" variant="secondary" size="sm" onClick={() => setShowAcct(v => !v)} aria-pressed={showAcct}>{showAcct ? "Hide" : "Show"}</Button>
                            </div>
                        </Field>
                        <Field label="Branch" hint="Optional"><Input value={f.bank_branch} onChange={set("bank_branch")} /></Field>
                    </div>
                    <div style={grid}>
                        <div><div style={text.label}>Current payment QR</div><ImagePreview path={settings.qr_path} alt="Payment QR" /></div>
                        <DocumentUploader label="Replace payment QR (PNG, JPEG or WebP, max 2 MB)" accept="image/png,image/jpeg,image/webp" maxMB={2} file={qr} onChange={setQr} />
                    </div>
                </FormSection>
            </SectionCard>

            <SectionCard title="Defaults" subtitle="Used when a new quotation or invoice is created. Each document can still be changed.">
                <FormSection>
                    <div style={grid}>
                        <Field label="Invoice payment terms (days)" error={errors.payment_terms_days}><Input inputMode="numeric" value={f.payment_terms_days} onChange={set("payment_terms_days")} /></Field>
                        <Field label="Quotation validity (days)" error={errors.quote_validity_days}><Input inputMode="numeric" value={f.quote_validity_days} onChange={set("quote_validity_days")} /></Field>
                    </div>
                    <Field label="Default terms" hint="Printed on documents unless changed there."><TextArea value={f.default_terms} onChange={set("default_terms")} /></Field>
                </FormSection>
            </SectionCard>

            <SectionCard title="Document numbering" subtitle="Numbers are assigned by the database only when a document is issued. Drafts have no number; cancelled numbers are never reused.">
                {fy === null ? <Notice tone="warning">Today's Nepali calendar month isn't verified, so the current fiscal year can't be shown.</Notice>
                    : <DetailList items={[["Fiscal year", fy || "…"], ["Next invoice number", next("INV")], ["Next quotation number", next("QT")]]} />}
            </SectionCard>

            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", position: "sticky", bottom: 0, padding: "12px 0", background: T.bg || "transparent" }}>
                <Button onClick={save} disabled={!dirty || invalid || busy}>{busy ? "Saving…" : "Save settings"}</Button>
                {dirty && !busy && <Button variant="secondary" onClick={() => { setF(initial); setLogo(null); setQr(null); setStamp(null); setErr(""); }}>Discard changes</Button>}
                {ok && <span style={{ color: T.success, fontSize: 13 }}>{ok}</span>}
                {err && <Notice tone="danger">{err}</Notice>}
                <span style={{ ...text.caption, marginLeft: "auto" }}>Last saved {settings.updated_at ? fmtDateTime(settings.updated_at) : "—"}</span>
            </div>
        </div>
    );
}
